/* ============================================================
   MARTEYA — BOLD LUXE motion engine
   Self-contained. Guards every effect behind reduced-motion and
   pointer capability. Safe to run once per page load.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initReveals();
    initMarquees();
    if (!reduce) {
      initParallax();
      initShowcase();
      if (finePointer) initCursor();
    } else {
      // Static fallback for horizontal showcase
      document.querySelectorAll('.lx-show').forEach(function (s) {
        s.classList.add('lx-show--static');
      });
    }
    initReviews();
    initHeroIntro();
    initHeroSlideshow();
    // Footer accordions live in footer.liquid so they work on every page,
    // not just the home page where this motion engine loads.
  });

  /* ---- Hero slideshow ---- */
  function initHeroSlideshow() {
    document.querySelectorAll('.lx-hero--slideshow').forEach(function (hero) {
      var slides = [].slice.call(hero.querySelectorAll('.lx-hero__slide'));
      var dots = [].slice.call(hero.querySelectorAll('.lx-hero__dot'));
      if (slides.length < 2) return;
      var i = 0, timer = null;
      var speed = (parseInt(hero.getAttribute('data-hero-autoplay'), 10) || 6) * 1000;
      var canAutoplay = hero.hasAttribute('data-hero-autoplay') && !reduce;

      function go(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      }
      function next() { go(i + 1); }
      function prev() { go(i - 1); }
      function start() { if (canAutoplay) { stop(); timer = setInterval(next, speed); } }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); start(); }); });
      var nextBtn = hero.querySelector('.lx-hero__arrow--next');
      var prevBtn = hero.querySelector('.lx-hero__arrow--prev');
      if (nextBtn) nextBtn.addEventListener('click', function () { next(); start(); });
      if (prevBtn) prevBtn.addEventListener('click', function () { prev(); start(); });
      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);

      /* ---- Drag / swipe to change slides ---- */
      var slidesEl = hero.querySelector('.lx-hero__slides') || hero;
      var startX = 0, startY = 0, dx = 0, dragging = false, horizontal = false, moved = false;
      var THRESHOLD = 48;   // px to commit a slide change
      var RESIST = 0.32;    // how far the slide follows the finger

      function activeSlide() { return slides[i]; }

      function onDown(e) {
        // ignore drags that begin on interactive controls
        if (e.target.closest('.lx-hero__arrow, .lx-hero__dot')) return;
        if (e.button != null && e.button !== 0) return; // primary button only
        dragging = true; horizontal = false; moved = false;
        startX = e.clientX; startY = e.clientY; dx = 0;
        stop(); // pause autoplay while interacting
        hero.classList.add('is-grabbing');
      }
      function onMove(e) {
        if (!dragging) return;
        dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!horizontal) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dx) <= Math.abs(dy)) { dragging = false; hero.classList.remove('is-grabbing'); return; } // vertical scroll — bail
          horizontal = true;
          try { slidesEl.setPointerCapture && slidesEl.setPointerCapture(e.pointerId); } catch (err) {}
        }
        if (horizontal) {
          moved = true;
          if (e.cancelable) e.preventDefault();
          var s = activeSlide();
          s.style.transition = 'none';
          s.style.transform = 'translate3d(' + (dx * RESIST) + 'px,0,0)';
        }
      }
      function settle() {
        var s = activeSlide();
        s.style.transition = 'transform 0.45s var(--lx-ease, cubic-bezier(0.19,1,0.22,1))';
        s.style.transform = '';
        setTimeout(function () { s.style.transition = ''; s.style.transform = ''; }, 480);
      }
      function onUp() {
        if (!dragging) return;
        dragging = false;
        hero.classList.remove('is-grabbing');
        if (horizontal) {
          if (dx <= -THRESHOLD) { settle(); next(); }
          else if (dx >= THRESHOLD) { settle(); prev(); }
          else { settle(); }
        }
        start(); // resume autoplay
      }

      if (window.PointerEvent) {
        slidesEl.addEventListener('pointerdown', onDown);
        slidesEl.addEventListener('pointermove', onMove);
        slidesEl.addEventListener('pointerup', onUp);
        slidesEl.addEventListener('pointercancel', onUp);
      }
      // Swallow the click that follows a real drag so a swipe never navigates
      slidesEl.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);

      start();
    });
  }

  /* ---- Scroll reveals ---------------------------------------------------- */
  function initReveals() {
    var items = document.querySelectorAll('.lx-reveal, .lx-reveal-group');
    if (!items.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        // stagger children of a group
        if (el.classList.contains('lx-reveal-group')) {
          var kids = el.querySelectorAll('.lx-stagger');
          kids.forEach(function (k, i) {
            k.style.setProperty('--lx-delay', (i * 0.08) + 's');
            k.classList.add('is-in');
          });
        }
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- Hero intro on load ------------------------------------------------ */
  function initHeroIntro() {
    var hero = document.querySelector('.lx-hero');
    if (!hero) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add('lx-in', 'is-in'); });
    });
  }

  /* ---- Parallax ---------------------------------------------------------- */
  function initParallax() {
    var els = [].slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var host = el.closest('[data-parallax-host]') || el.parentElement;
        var r = host.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
        var progress = (r.top + r.height / 2 - vh / 2) / vh; // -1..1 ish
        el.style.transform = 'translate3d(0,' + (progress * speed * 100).toFixed(2) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---- Marquee: duplicate content for seamless loop ---------------------- */
  function initMarquees() {
    document.querySelectorAll('.lx-marquee__track').forEach(function (track) {
      if (track.dataset.dup) return;
      track.dataset.dup = '1';
      // Clone existing nodes (not innerHTML) for a seamless -50% loop.
      [].slice.call(track.children).forEach(function (node) {
        track.appendChild(node.cloneNode(true));
      });
    });
  }

  /* ---- Horizontal scroll showcase --------------------------------------- */
  function initShowcase() {
    var shows = [].slice.call(document.querySelectorAll('.lx-show'));
    if (!shows.length) return;
    var touch = window.matchMedia('(max-width: 900px)').matches
      || window.matchMedia('(pointer: coarse)').matches;
    shows.forEach(function (show) {
      var pin = show.querySelector('.lx-show__pin');
      var track = show.querySelector('.lx-show__track');
      if (!pin || !track) return;
      if (touch) { show.classList.add('lx-show--static'); pin.style.height = ''; return; }

      function sizePin() {
        var distance = track.scrollWidth - window.innerWidth;
        if (distance < 40) {
          // not enough to scroll horizontally → static row
          show.classList.add('lx-show--static');
          pin.style.height = '';
          return distance;
        }
        show.classList.remove('lx-show--static');
        pin.style.height = (window.innerHeight + distance) + 'px';
        return distance;
      }
      var distance = sizePin();

      var ticking = false;
      function update() {
        distance = track.scrollWidth - window.innerWidth;
        if (distance < 40) { ticking = false; return; }
        var r = pin.getBoundingClientRect();
        var scrolled = Math.min(Math.max(-r.top, 0), pin.offsetHeight - window.innerHeight);
        track.style.transform = 'translate3d(' + (-scrolled) + 'px,0,0)';
        ticking = false;
      }
      function onScroll() {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', function () { sizePin(); update(); });
      update();
    });
  }

  /* ---- Reviews rotator --------------------------------------------------- */
  function initReviews() {
    document.querySelectorAll('.lx-rev').forEach(function (rev) {
      var slides = [].slice.call(rev.querySelectorAll('.lx-rev__slide'));
      var dots = [].slice.call(rev.querySelectorAll('.lx-rev__dot'));
      if (slides.length < 2) return;
      var i = 0, timer;
      function show(n) {
        slides.forEach(function (s, k) { s.hidden = k !== n; });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === n); });
        i = n;
      }
      function next() { show((i + 1) % slides.length); }
      function start() { if (!reduce) timer = setInterval(next, 5000); }
      function stop() { clearInterval(timer); }
      dots.forEach(function (d, k) {
        d.addEventListener('click', function () { stop(); show(k); start(); });
      });
      rev.addEventListener('mouseenter', stop);
      rev.addEventListener('mouseleave', start);
      show(0); start();
    });
  }

  /* ---- Custom cursor ----------------------------------------------------- */
  function initCursor() {
    var ring = document.createElement('div');
    ring.className = 'lx-cursor';
    var dot = document.createElement('div');
    dot.className = 'lx-cursor-dot';
    document.body.appendChild(ring);
    document.body.appendChild(dot);
    document.body.classList.add('lx-has-cursor');

    var rx = 0, ry = 0, x = 0, y = 0;
    document.addEventListener('mousemove', function (e) {
      x = e.clientX; y = e.clientY;
      dot.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%)';
    });
    function loop() {
      rx += (x - rx) * 0.18;
      ry += (y - ry) * 0.18;
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    }
    loop();

    var hoverSel = 'a, button, .lx-prod, .lx-show__card, input, [data-cursor-hover]';
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(hoverSel)) ring.classList.add('is-hover');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(hoverSel)) ring.classList.remove('is-hover');
    });
    document.addEventListener('mouseleave', function () {
      ring.style.opacity = '0'; dot.style.opacity = '0';
    });
    document.addEventListener('mouseenter', function () {
      ring.style.opacity = '1'; dot.style.opacity = '1';
    });
  }
})();
